import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export type Role = "admin" | "author" | "moderator";

export interface AuthUser {
  username: string;
  roles: Role[];
}

interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  updateProfile: (params: { currentPassword: string; newUsername?: string; newPassword?: string }) => Promise<void>;
  logout: () => void;
  hasRole: (...roles: Role[]) => boolean;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "auth_token";
// Passe das ggf. an eure Umgebungsvariable an, z.B. process.env.REACT_APP_API_BASE_URL
const API_BASE = process.env.REACT_APP_API_BASE_URL ?? "";

// Liest username/roles direkt aus dem JWT-Payload aus (ohne Signaturpruefung -
// das macht ohnehin das Backend bei jeder Anfrage). Reicht, um nach einem
// Seiten-Reload den eingeloggten Zustand wiederherzustellen.
function decodeToken(token: string): AuthUser | null {
  try {
    const payloadBase64 = token.split(".")[1];
    const payload = JSON.parse(atob(payloadBase64));
    if (!payload.username || !Array.isArray(payload.roles)) return null;
    return { username: payload.username, roles: payload.roles };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? decodeToken(stored) : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "Login fehlgeschlagen");
      }

      const data = await res.json();
      setToken(data.token);
      setUser({ username: data.username, roles: data.roles });
      localStorage.setItem(STORAGE_KEY, data.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler beim Login");
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const hasRole = useCallback(
    (...roles: Role[]) => !!user && user.roles.some((r) => roles.includes(r)),
    [user]
  );

  const register = useCallback(async (username: string, password: string) => {
  setIsLoading(true);
  setError(null);
  try {
    const res = await fetch(`${API_BASE}/api/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error ?? "Registrierung fehlgeschlagen");
    }
    // Nach erfolgreicher Registrierung direkt einloggen, damit der Flow nahtlos ist
    await login(username, password);
  } catch (err) {
    setError(err instanceof Error ? err.message : "Unbekannter Fehler bei der Registrierung");
    throw err;
  } finally {
    setIsLoading(false);
  }
}, [login]);

const updateProfile = useCallback(async (params: { currentPassword: string; newUsername?: string; newPassword?: string }) => {
  if (!token) throw new Error("Nicht angemeldet");
  setIsLoading(true);
  setError(null);
  try {
    const res = await fetch(`${API_BASE}/api/me`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(params),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error ?? "Aktualisierung fehlgeschlagen");
    }
    const data = await res.json();
    setToken(data.token);
    setUser({ username: data.username, roles: data.roles });
    localStorage.setItem(STORAGE_KEY, data.token);
  } catch (err) {
    setError(err instanceof Error ? err.message : "Unbekannter Fehler beim Aktualisieren");
    throw err;
  } finally {
    setIsLoading(false);
  }
}, [token]);

return (
  <AuthContext.Provider value={{ user, token, login, register, updateProfile, logout, hasRole, isLoading, error }}>
    {children}
  </AuthContext.Provider>
);
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth muss innerhalb von <AuthProvider> verwendet werden");
  return ctx;
}
